import type {
  ExtractedField,
  ExtractionError,
  ExtractionWarning,
} from "../types";

type PdfExtractionResult = {
  fields: Record<string, ExtractedField>;
  errors: ExtractionError[];
  warnings: ExtractionWarning[];
};

const PDF_CELL = "pdf";
const PDF_SHEET = "_pdf_text";

const FIELD_PATTERNS: {
  field: string;
  pattern: RegExp;
  group: number;
  postProcess?: (value: string) => unknown;
}[] = [
  { field: "decisionNumber", pattern: /nr\.\s*(\d+)\s+din/i, group: 1 },
  { field: "decisionDate", pattern: /nr\.\s*\d+\s+din\s+([\d.]+)/i, group: 1 },
  {
    field: "entityName",
    pattern: /entit[ăa][tț]ii\s+(.+?)\s+cu sediul/i,
    group: 1,
  },
  {
    field: "address",
    pattern: /cu sediul [iî]n\s+(.+?),\s*(?:jude[tț]ul|jud\.)/i,
    group: 1,
  },
  {
    field: "county",
    pattern: /(?:jude[tț]ul|jud\.)\s+(.+?),\s*CUI/i,
    group: 1,
  },
  { field: "cui", pattern: /CUI\s*(\d+)/i, group: 1 },
  { field: "category", pattern: /categoria\s+(.+?)\s*\(/i, group: 1 },
  { field: "categoryCode", pattern: /\(([A-Z]{2})\)/, group: 1 },
  {
    field: "registrationNumber",
    pattern: /num[aă]rul\s+(E-[A-Z0-9]+)/i,
    group: 1,
  },
  {
    field: "sectors",
    pattern: /sectoarele:\s*(.+?)\./i,
    group: 1,
    postProcess: (value: string) =>
      value
        .split(/(?:,\s*|\s+[sșSȘ][iI]\s+)(?=[A-ZĂÂÎȘȚ])/)
        .map((s) => s.trim())
        .filter(Boolean),
  },
];

export function extractFieldsFromPdfText(
  text: string,
  fileName: string
): PdfExtractionResult {
  const fields: Record<string, ExtractedField> = {};
  const errors: ExtractionError[] = [];
  const warnings: ExtractionWarning[] = [];

  for (const { field, pattern, group, postProcess } of FIELD_PATTERNS) {
    const match = text.match(pattern);

    if (!match || !match[group]) {
      warnings.push({
        file: fileName,
        field,
        code: "EMPTY_VALUE",
        message: `Could not extract "${field}" from PDF text`,
      });
      continue;
    }

    const rawValue = match[group].trim();
    const parsedValue = postProcess ? postProcess(rawValue) : rawValue;

    fields[field] = {
      field,
      rawValue,
      parsedValue,
      cell: PDF_CELL,
      sheetName: PDF_SHEET,
    };
  }

  return { fields, errors, warnings };
}
